// Pequeño wrapper de la Screen Wake Lock API. La spec: mantener pantalla encendida solo mientras
// el seguimiento GPS esté activo; liberarlo al desactivar seguimiento o al ocultarse la página.
//
// El API es opcional en el navegador (`navigator.wakeLock`) y puede lanzar; envolvemos todo en
// try/catch. Si no está disponible, el sistema simplemente no bloquea el apagado y no pasa nada.

type Sentinel = { release: () => Promise<void>; released: boolean } | null;

// Tipos mínimos que necesitamos, sin depender de lib.dom actualizada.
interface WakeLockApi {
  request(kind: "screen"): Promise<{
    release: () => Promise<void>;
    released: boolean;
    addEventListener(type: "release", cb: () => void): void;
  }>;
}

const getApi = (): WakeLockApi | null => {
  const nav = navigator as unknown as { wakeLock?: WakeLockApi };
  return nav.wakeLock ?? null;
};

let sentinel: Sentinel = null;

async function acquire(): Promise<void> {
  const api = getApi();
  if (!api) return;
  if (sentinel && !sentinel.released) return;
  try {
    const s = await api.request("screen");
    sentinel = s;
    s.addEventListener("release", () => {
      sentinel = null;
    });
  } catch {
    // Permiso denegado o página no en foreground: ignorar en silencio.
  }
}

async function release(): Promise<void> {
  if (!sentinel) return;
  try {
    await sentinel.release();
  } catch {
    // idem
  }
  sentinel = null;
}

// Uso: llama a keepAwake(true) al empezar el seguimiento GPS, y keepAwake(false) al pararlo.
// Automáticamente vuelve a intentar el lock cuando la pestaña vuelve a foreground.
let wanted = false;
let bound = false;
export function keepAwake(active: boolean): void {
  wanted = active;
  if (!bound) {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void release();
      else if (wanted) void acquire();
    });
    bound = true;
  }
  if (wanted) void acquire();
  else void release();
}
